import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Icon } from '@iconify/react';
import style from '../../scss/table.module.scss';
import TablePagination from './TablePagination';

/**
 * 100% reusable, config driven table.
 *
 * Column config (each item of `columns`):
 *   key            {string}    Unique key, also used to read the value from a row.
 *   label          {node}      Header label.
 *   sticky         {boolean}   Pins the column to the RIGHT edge on horizontal scroll.
 *                              Pinned columns MUST declare a numeric `width` so the
 *                              stacking offset can be calculated.
 *   width          {number}    Fixed column width in px (required for sticky columns).
 *   align          {'left'|'center'|'right'}  Cell alignment (default 'center').
 *   headerColor    {string}    Background color of the header cell (e.g. "#243D71").
 *   headerTextColor{string}    Text color of the header cell.
 *   cellColor      {string}    Background color of the body cells.
 *   cellTextColor  {string}    Text color of the body cells.
 *   headerClassName{string}    Extra class for the header cell.
 *   cellClassName  {string}    Extra class for body cells.
 *   render         {(value,row,rowIndex)=>node}  Custom cell renderer.
 *
 * @param {Array}  columns        Column configuration (see above).
 * @param {Array}  data           Row data.
 * @param {boolean} loading       Shows the skeleton placeholder when true.
 * @param {number} skeletonRows   Amount of skeleton rows while loading (default 5).
 * @param {string|Function} rowKey  Row key field or a `(row, index) => key` resolver.
 * @param {object} emptyState     { icon, title, text, action } shown when there is no data.
 * @param {object} pagination     { page, totalPages, onPageChange, label } (optional).
 * @param {string} className      Extra class for the outer wrapper.
 */
const TableComponent = ({
    columns = [],
    data = [],
    loading = false,
    skeletonRows = 5,
    rowKey = 'id',
    emptyState = {},
    pagination = null,
    className = '',
}) => {
    // Index of the left-most pinned column -> gets the scroll shadow hint.
    const firstStickyIndex = columns.findIndex((col) => col.sticky);

    // Right offset for a pinned column = sum of widths of the pinned columns after it.
    const stickyStyle = (index) => {
        const col = columns[index];
        if (!col.sticky) return undefined;
        let right = 0;
        for (let i = index + 1; i < columns.length; i += 1) {
            if (columns[i].sticky) right += columns[i].width || 0;
        }
        return { position: 'sticky', right, zIndex: 2 };
    };

    const widthStyle = (col) => (col.width ? { width: col.width, minWidth: col.width } : undefined);

    const headerColorStyle = (col) => ({ backgroundColor: col.headerColor, color: col.headerTextColor });

    const cellColorStyle = (col) => ({ backgroundColor: col.cellColor, color: col.cellTextColor });

    const alignClass = (align) => {
        if (align === 'left') return style.alignLeft;
        if (align === 'right') return style.alignRight;
        return '';
    };

    const cellClasses = (col, index, base) => [
        base,
        alignClass(col.align),
        col.sticky ? style.sticky : '',
        col.sticky && index === firstStickyIndex ? style.stickyShadow : '',
        col.cellClassName || '',
    ].filter(Boolean).join(' ');

    const resolveKey = (row, index) => {
        if (typeof rowKey === 'function') return rowKey(row, index);
        return row?.[rowKey] ?? index;
    };

    const hasData = Array.isArray(data) && data.length > 0;

    return (
        <div className={`${style.wrapper} ${className}`.trim()}>
            <div className={style.scroll}>
                <table className={style.table}>
                    <thead>
                        <tr className={style.headRow}>
                            {columns.map((col, i) => (
                                <th
                                    key={col.key ?? i}
                                    className={[
                                        style.headCell,
                                        i === 0 ? style.headCellFirst : '',
                                        i === columns.length - 1 ? style.headCellLast : '',
                                        alignClass(col.align),
                                        col.sticky ? style.sticky : '',
                                        col.sticky && i === firstStickyIndex ? style.stickyShadow : '',
                                        col.headerClassName || '',
                                    ].filter(Boolean).join(' ')}
                                    style={{ ...stickyStyle(i), ...widthStyle(col), ...headerColorStyle(col), zIndex: col.sticky ? 3 : 1 }}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    {/* 8px gap between the header and the body (own tbody keeps zebra parity) */}
                    {(loading || hasData) && (
                        <tbody className={style.headerGap} aria-hidden="true">
                            <tr><td colSpan={columns.length} /></tr>
                        </tbody>
                    )}

                    {loading ? (
                        <tbody>
                            {Array.from({ length: skeletonRows }).map((_, r) => (
                                <tr key={`sk-${r}`} className={style.bodyRow}>
                                    {columns.map((col, i) => (
                                        <td
                                            key={col.key ?? i}
                                            className={cellClasses(col, i, style.cell)}
                                            style={{ ...stickyStyle(i), ...widthStyle(col), ...cellColorStyle(col) }}
                                        >
                                            <Skeleton height={16} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ) : hasData ? (
                        <tbody>
                            {data.map((row, rowIndex) => (
                                <tr key={resolveKey(row, rowIndex)} className={style.bodyRow}>
                                    {columns.map((col, i) => (
                                        <td
                                            key={col.key ?? i}
                                            className={cellClasses(col, i, style.cell)}
                                            style={{ ...stickyStyle(i), ...widthStyle(col), ...cellColorStyle(col) }}
                                        >
                                            {col.render ? col.render(row?.[col.key], row, rowIndex) : row?.[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ) : null}
                </table>

                {!loading && !hasData && (
                    <div className={style.empty}>
                        <Icon className={style.emptyIcon} icon={emptyState.icon || 'mdi:table-off'} />
                        <p className={style.emptyTitle}>{emptyState.title || 'No records found'}</p>
                        {emptyState.text && <p className={style.emptyText}>{emptyState.text}</p>}
                        {emptyState.action}
                    </div>
                )}
            </div>

            {pagination && !loading && hasData && (
                <TablePagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={pagination.onPageChange}
                    label={pagination.label}
                />
            )}
        </div>
    );
};

export default TableComponent;
